import React, { useCallback, useEffect, useState } from "react";
import { C } from "../theme";
import Button from "../components/Button";
import { auth } from "../firebase";
import { resolveApiBaseUrl, resolveHostedAppBaseUrl } from "../services/apiBase";

async function requestAuthEmail({ action, email, continueUrl }) {
  const base = resolveApiBaseUrl() || "";
  const res = await fetch(`${base}/v1/auth/email`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action, email, continueUrl, publicBaseUrl: continueUrl }),
  });
  if (!res.ok) {
    let detail = "";
    try {
      const payload = await res.json();
      detail = payload?.error?.message || "";
    } catch (_) {}
    throw new Error(detail || `Email request failed (${res.status}).`);
  }
  return res.json();
}

export default function VerifyEmailScreen({ user, onSignOut }) {
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const email = user?.email || "";

  const handleResend = useCallback(async () => {
    if (!email || sending) return;
    setSending(true);
    setError("");
    setNotice("");
    try {
      const continueUrl = resolveHostedAppBaseUrl() || window.location.origin;
      await requestAuthEmail({ action: "signin_link", email, continueUrl });
      setNotice(`Verification link sent to ${email}. Check your inbox (and spam).`);
    } catch (err) {
      setError(err?.message || "Could not send verification email.");
    } finally {
      setSending(false);
    }
  }, [email, sending]);

  const handleCheckVerified = useCallback(async () => {
    if (checking) return;
    setChecking(true);
    setError("");
    setNotice("");
    try {
      await auth.currentUser?.reload();
      await auth.currentUser?.getIdToken(true);
      if (!auth.currentUser?.emailVerified) {
        setError("Still not verified. Click the link in your email, then try again.");
      }
    } catch (err) {
      setError(err?.message || "Could not refresh status.");
    } finally {
      setChecking(false);
    }
  }, [checking]);

  // Auto-refresh on window focus so users coming back from the email link
  // pick up the verified state without a manual click.
  useEffect(() => {
    const onFocus = () => {
      auth.currentUser?.reload().catch(() => {});
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", padding: "20px 16px 90px" }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: C.teal, letterSpacing: 3, marginBottom: 6 }}>THE 99</div>
        <div style={{ fontSize: 22, color: C.white, fontFamily: "Georgia,serif", fontWeight: 700 }}>
          Verify Your Email
        </div>
      </div>

      <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, marginBottom: 14 }}>
        We need to confirm <span style={{ color: C.white }}>{email || "your email"}</span> belongs to you
        before you can use the app. Tap the link in your verification email, then return here.
      </div>

      {notice && (
        <div style={{ padding: "10px 12px", borderRadius: 8, background: `${C.teal}22`, border: `1px solid ${C.teal}55`, color: C.white, fontSize: 12, marginBottom: 12 }}>
          {notice}
        </div>
      )}
      {error && (
        <div style={{ padding: "10px 12px", borderRadius: 8, background: "rgba(255,90,90,0.10)", border: `1px solid ${C.danger || "#c0392b"}55`, color: C.white, fontSize: 12, marginBottom: 12 }}>
          {error}
        </div>
      )}

      <Button variant="primary" fullWidth onClick={handleCheckVerified} disabled={checking} style={{ marginBottom: 10 }}>
        {checking ? "Checking..." : "I've verified — refresh"}
      </Button>
      <Button variant="secondary" fullWidth onClick={handleResend} disabled={sending || !email} style={{ marginBottom: 10 }}>
        {sending ? "Sending..." : "Resend verification email"}
      </Button>
      <Button variant="ghost" fullWidth onClick={onSignOut} style={{ marginBottom: 10 }}>
        Sign Out
      </Button>
    </div>
  );
}
